import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import { reviewsService } from '../../shared/services/reviews.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import type { Booking } from '../../shared/services/bookings.service';

type Props = {
  booking: Booking | null;
  onClose: () => void;
  onSubmitted: () => void;
};

/**
 * Dialog the customer opens from the bookings dashboard to leave a review on a
 * past confirmed booking. The server enforces eligibility (CONFIRMED status,
 * past date, one review per booking) — the UI guards against the easy mistakes
 * but doesn't replicate the rules.
 */
export function LeaveReviewDialog({ booking, onClose, onSubmitted }: Props) {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const { notify } = useNotification();

  const [rating, setRating] = useState<number>(0);
  const [hover, setHover] = useState<number>(0);
  const [comment, setComment] = useState('');

  const reset = () => {
    setRating(0);
    setHover(0);
    setComment('');
  };

  const mutation = useMutation({
    mutationFn: () =>
      reviewsService.create(booking!.provider.id, {
        bookingId: booking!.id,
        rating,
        comment: comment.trim() || undefined,
      }),
    onSuccess: () => {
      notify.success(t('reviews.submitSuccess'));
      reset();
      onSubmitted();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const close = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const shown = hover || rating;

  return (
    <Dialog open={!!booking} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {t('reviews.dialogTitle', { provider: booking?.provider.businessName ?? '' })}
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t('reviews.dialogSubtitle')}
        </Typography>

        <Box sx={{ textAlign: 'center', my: 2 }}>
          <Stack direction="row" justifyContent="center" spacing={0.5}>
            {[1, 2, 3, 4, 5].map((n) => (
              <IconButton
                key={n}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                aria-label={t('reviews.starAria', { n })}
                size="large"
                sx={{ p: 0.5 }}
              >
                {n <= shown ? (
                  <StarRoundedIcon
                    sx={{ fontSize: '2.25rem', color: '#FFB400' }}
                  />
                ) : (
                  <StarOutlineRoundedIcon
                    sx={{ fontSize: '2.25rem', color: 'text.disabled' }}
                  />
                )}
              </IconButton>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {shown > 0 ? t(`reviews.ratingLabel.${shown}`) : t('reviews.starHint')}
          </Typography>
        </Box>

        <TextField
          label={t('reviews.commentLabel')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          inputProps={{ maxLength: 2000 }}
          helperText={`${comment.length} / 2000`}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={rating < 1 || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? t('common.loading') : t('reviews.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
